import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { listAccounts } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const AccountContext = createContext(null);
const LS_KEY = "kwl.selected_account";

export function AccountProvider({ children }) {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState(
    typeof localStorage !== "undefined" ? localStorage.getItem(LS_KEY) : null
  );
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setAccounts([]);
      return;
    }
    setLoading(true);
    try {
      const list = await listAccounts();
      setAccounts(list);
      // Ensure selectedId still valid
      if (!list.find((a) => a.account_id === selectedId)) {
        const next = list[0]?.account_id || null;
        setSelectedId(next);
        if (next) localStorage.setItem(LS_KEY, next);
      }
    } finally {
      setLoading(false);
    }
  }, [user, selectedId]);

  useEffect(() => { refresh(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (id) => {
    setSelectedId(id);
    if (id) localStorage.setItem(LS_KEY, id);
  };

  const selected = accounts.find((a) => a.account_id === selectedId) || null;

  return (
    <AccountContext.Provider value={{ accounts, selectedId, selected, select, refresh, loading }}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccounts() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error("useAccounts must be used within AccountProvider");
  return ctx;
}
